import type { Vec2 } from '../core/math';

export type PresenceFighter = Vec2 & { id: number; team?: number };
export type PresenceShot = Vec2 & { owner: number; vx: number; vy: number };

/** A brief anticipatory reaction to a passing shot; this never influences the simulation. */
export function incomingCloseCall(fighter: PresenceFighter, shots: PresenceShot[]): boolean {
  return shots.some((shot) => {
    if (shot.owner === fighter.id) return false;
    const dx = shot.x - fighter.x, dy = shot.y - fighter.y;
    if (dx * dx + dy * dy > 16) return false;
    const speed2 = shot.vx * shot.vx + shot.vy * shot.vy;
    const approach = dx * shot.vx + dy * shot.vy;
    if (speed2 < 1 || approach >= 0) return false;
    const time = Math.min(0.12, -approach / speed2);
    return Math.hypot(dx + shot.vx * time, dy + shot.vy * time) < 1.05;
  });
}

export function nearbyOpponent(fighter: PresenceFighter, fighters: PresenceFighter[]): Vec2 | undefined {
  let closest = 8 ** 2, target: PresenceFighter | undefined;
  for (const other of fighters) {
    if (other.id === fighter.id || (fighter.team !== undefined && other.team === fighter.team)) continue;
    const distance = (other.x - fighter.x) ** 2 + (other.y - fighter.y) ** 2;
    if (distance > 0.01 && distance < closest) { closest = distance; target = other; }
  }
  if (!target) return;
  const distance = Math.sqrt(closest);
  return { x: (target.x - fighter.x) / distance, y: (target.y - fighter.y) / distance };
}
